import { useEffect, useState } from 'react';

import { EVENT_REDIRECT_BUCKET } from '@/lib/detail/event-detail';
import { EVENT_SHOW_BUCKET } from '@todo/core/detail/event-detail';
import { buildShow, isEventUuid, type EventShow } from '@todo/core/event-show';
import { getPrefetched } from '@todo/core/static-prefetch';
import { EVENT_COLUMNS, supabase, type DbEvent } from '@todo/core/supabase';

export type EventShowState = {
  show: EventShow | null;
  /** Set when `param` is an old per-date row id: the path under /[lang] to redirect to. */
  redirectPath: string | null;
  loading: boolean;
  error: string | null;
};

type Fetched = { param: string; show: EventShow | null; redirectPath: string | null; error: string | null };

/**
 * The show behind an /[lang]/events/[id] URL. `param` is normally a show_slug;
 * an old per-date row id resolves to `redirectPath` instead (event-detail.ts).
 *
 * Until the client fetch lands, it returns the record the static export baked
 * into the page (getPrefetched — during the export itself, the one
 * generateStaticParams stashed), so the first render matches the HTML. Results
 * are keyed by `param`, so a screen reused for another show never shows the
 * previous one's data.
 */
export function useEventShow(param: string | undefined): EventShowState {
  const isUuid = isEventUuid(param);
  const [fetched, setFetched] = useState<Fetched | null>(null);

  useEffect(() => {
    if (!param) return;
    let cancelled = false;
    const done = (f: Omit<Fetched, 'param'>) => {
      if (!cancelled) setFetched({ param, ...f });
    };

    if (isUuid) {
      // A baked redirect needs no lookup; an unbaked one (client-side
      // navigation to an old link) asks which show the row belonged to, from
      // the frozen map that outlives the row.
      if (getPrefetched<string>(EVENT_REDIRECT_BUCKET, param)) return;
      supabase
        .from('event_legacy_urls')
        .select('show_slug')
        .eq('id', param)
        .maybeSingle()
        .then(({ data, error }) =>
          done({
            show: null,
            redirectPath: data ? `events/${(data as { show_slug: string }).show_slug}` : null,
            error: error?.message ?? null,
          }),
        );
    } else {
      supabase
        .from('events_visible')
        .select(EVENT_COLUMNS)
        .eq('show_slug', param)
        .then(({ data, error }) =>
          done({
            show: error ? null : buildShow((data ?? []) as DbEvent[]),
            redirectPath: null,
            error: error?.message ?? null,
          }),
        );
    }

    return () => {
      cancelled = true;
    };
  }, [param, isUuid]);

  if (!param) return { show: null, redirectPath: null, loading: false, error: null };

  const current = fetched?.param === param ? fetched : null;
  const baked = isUuid
    ? { show: null, redirectPath: getPrefetched<string>(EVENT_REDIRECT_BUCKET, param) ?? null }
    : { show: getPrefetched<EventShow>(EVENT_SHOW_BUCKET, param) ?? null, redirectPath: null };

  // A failed refresh keeps the baked record on screen rather than blanking it.
  const show = current?.show ?? baked.show;
  const redirectPath = current?.redirectPath ?? baked.redirectPath;
  return {
    show,
    redirectPath,
    loading: !current && !show && !redirectPath,
    error: current?.error ?? null,
  };
}
